import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, Building2, Eye, EyeOff, Loader2, RefreshCw } from 'lucide-react';
import { apiFetch } from '@/stores/authStore';
import { feedEndpointLabel } from '@/lib/infra/feedEndpointLabel';
import { parseOrdersSnapshot } from '@/stores/accountStore';
import { useTickerFeedNeeds } from '@/hooks/useTickerFeedNeeds';
import { useMarketStore } from '@/stores/marketStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { describeMarket } from '@/lib/kalshiTicker';
import {
  sportFromTicker,
  sportMatchesFilter,
  cycleSportFilter,
  type SportFilter,
} from '@/lib/kalshiTicker/sportFilter';
import { SPORT_TABS, type Sport } from '@/views/ValueDashboard/types';
import {
  SummaryTile,
  SummaryStrip,
  ViewModeToggle,
  CardGrid,
  CompactListGrid,
  MarketTypeChip,
} from '@/components/cards/cardKit';
import {
  fmtCents,
  fmtLastFillAt,
  fmtUsd,
  fmtUsdSigned,
  titleParts,
} from '@/components/cards/cardFormat';
import { PositionCard, CompactPositionItem, PositionQty } from '@/components/cards/PositionCard';
import {
  buildPolyPositionVM,
  buildUsPositionVM,
  fullPositionAnchor,
  type PositionCardVM,
} from '@/components/cards/positionCardModel';
import { usPositionKey } from '@/lib/polymarketUs/portfolioModel';
import type { TradingVenue } from '@/lib/venues';
import { dash, UsFillPrice, UsFillTime, UsRetry } from '@/views/TraderDetail/usManagerReads';
import { US_CONFLICT_TITLE } from '@/lib/fills/usPrice';
import { formatUsDecimal, formatUsUsd } from '@/lib/polymarketUs/format';
import { usMarketLabel } from '@/lib/polymarketUs/marketLabel';
import { FilterNote } from '@/views/PolymarketUs/UsPortfolioPanel';
import { americanOdds } from '@/views/ValueDashboard/tradeModalBook';
import {
  computeOrgTraderUnrealized,
  type OrgTraderRow,
} from '@/components/organisms/OrgTradersTable';
import { describePolyMarketCached, polyPositionLiveData } from '@/lib/polymarket/describeOrder';
import { estimateAggregateFeeCents, takerRateForTicker } from '@/lib/pricing/fees';
import { usePolyFeeSchedules } from '@/hooks/usePolyFeeSchedules';
import type { PolyFeeSchedule } from '@/lib/polymarket/relay';
import {
  UNATTRIBUTED_OWNER,
  ownerMatchesFilter,
  resolveOwnerAttribution,
  type OwnerAttribution,
  type OwnerFilterKey,
} from '@/lib/ownerAttribution';
import {
  kalshiReportedBalanceTitle,
  readKalshiReportedBalanceCents,
} from '@/lib/kalshiBalance';
import { FORMER_TITLE, useUsOrgRows, type OrgUsFill, type OrgUsPosition } from './useUsOrgRows';
import { usReadStatus } from '@/lib/polymarketUs/readStatus';
import { useInstitutionalDeskOrders } from './useInstitutionalDeskOrders';
import { InstitutionalDeskOrderControls, InstitutionalDeskOrderRow } from './InstitutionalDeskOrders';
import { InstitutionalPrivateSnapshot } from './InstitutionalPrivateSnapshot';
import { useInstitutionalOrderStatus } from './useInstitutionalOrderStatus';
import { InstitutionalOrderStatus } from './InstitutionalOrderStatus';
import { addKnown, usOrgMemberEconomics, usOrgOwnerComplete, usOrgPositionEconomics } from './orgUsEconomics';
import type { UsPositionMarket } from '@/lib/polymarketUs/positionEconomics';
import {
  orgRowKey,
  reconciledOrgLifetimeSource,
  type OrgPositionRow,
} from './orgRowKey';
import {
  decodeOrgAccountPositions,
  decodeOrgFillRows,
  nextOrgPanel,
  orgAccountOwnerComplete,
  orgPanelTileCount,
  unavailableOrgPanel,
  unreadOrgPanel,
  type OrgAccountHoldings,
  type OrgReadPanel,
} from './orgKalshiReads';

// Same dense→table→card→mini choices as the Positions page. positionViewMode
// is a shared persisted preference, so the org view tracks whatever the trader
// last picked on /positions.
const POSITION_VIEW_MODES = ['compact', 'table', 'card', 'mini'] as const;

interface OrgDetailProps {
  /** Admin path: the org being inspected (sent as ?orgId). Omit for the
   *  manager "own org" path — the relay scopes to the caller's org. */
  orgId?: string;
  orgName?: string;
  /** Omit on the manager nav surface (no parent to go back to). */
  onBack?: () => void;
  onTraderClick: (row: OrgTraderRow) => void;
}

// One resting order on the org account, tagged with its owning trader
// (from /org/orders-by-trader — null owner = unattributed, the failsafe truth).
interface OrgOrderRow {
  orderId: string;
  owner: OwnerAttribution;
  ticker: string;
  side: 'yes' | 'no';
  qty: number;
  priceCents: number | null;
  sport: Sport | null;
}

interface OrgFillRow {
  fillId: string;
  orderId: string | null;
  owner: OwnerAttribution;
  venue: 'kalshi' | 'polymarket';
  ticker: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  qty: number;
  priceCents: number | null;
  feeCents: number | null;
  executedAt: string;
  sport: Sport | null;
}

// One sibling-instance position from /org/remote-positions (the relay's fold
// of remote-attributed fills). Rendered only when it explains part of a live
// venue position — settled markets drop out with the venue snapshot.
interface RemotePositionWire {
  surface: string;
  remoteUserId: string;
  remoteUsername: string;
  ticker: string;
  side: 'yes' | 'no';
  qty: number;
  avgCents: number;
  costBasisCents: number;
  feesPaidCents: number | null;
  lastFillAt: string | null;
}

// Per-sibling sync health from /org/attribution-sync-status.
interface SiblingSyncStatus {
  code: string;
  url: string;
  lastOkAt: string | null;
  lastError: string | null;
  rowsMirrored: number;
}

function SportFilterChip({
  mode,
  label,
  onClick,
}: {
  mode: 'off' | 'include' | 'exclude';
  label: string;
  onClick: () => void;
}) {
  const cls =
    mode === 'include'
      ? 'border-primary/50 bg-primary/15 text-primary'
      : mode === 'exclude'
        ? 'border-red-500/50 bg-red-500/10 text-red-400 line-through'
        : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${cls}`}
    >
      {label}
    </button>
  );
}

/** #964: an NFL ladder prop's badge carries the player and rung (#603: one
 *  line); its stat stacks under it so the side column stays badge-wide. */
function SideBadge({ side, betTeam, marketType }: {
  side: 'yes' | 'no'; betTeam: string | null; marketType?: string | null;
}) {
  const badge = (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
        side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
      }`}
    >
      {betTeam ? `(${betTeam}) ` : ''}
      {side.toUpperCase()}
    </span>
  );
  if (!marketType) return badge;
  return (
    <span className="inline-flex flex-col items-center gap-0.5">
      {badge}
      <MarketTypeChip marketType={marketType} />
    </span>
  );
}

/** `former` (#1055): a Polymarket US holding whose owner has left the org. */
function OwnerTag({ owner, former = false }: { owner: OwnerAttribution; former?: boolean }) {
  if (owner.kind === 'bot') {
    return (
      <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-400" title={owner.label ?? 'Bot-owned order'}>
        {owner.label ?? 'bot'}
      </span>
    );
  }
  if (owner.kind === 'remote') {
    return (
      <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-400" title="Placed by a trader on a sibling instance (shared org keys, separate DB) — attributed via the cross-instance ledger sync.">
        {owner.label ?? 'remote'}
      </span>
    );
  }
  if (owner.kind === 'unattributed') {
    return (
      <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-400" title="No matching trader attribution was found. This activity remains part of the organization’s venue account.">
        unattributed
      </span>
    );
  }
  if (former) return <span className="text-muted-foreground text-xs italic" title={FORMER_TITLE}>{owner.label}</span>;
  return <span className="text-foreground/90 text-xs">{owner.label}</span>;
}

const VENUE_TAG: Record<TradingVenue, { label: string; tone: string }> = {
  kalshi: { label: 'KALSHI', tone: 'bg-sky-500/15 text-sky-400 light:text-sky-700' },
  polymarket: { label: 'POLY', tone: 'bg-violet-500/15 text-violet-400 light:text-violet-700' },
  polymarket_us: { label: 'US', tone: 'bg-teal-500/15 text-teal-400 light:text-teal-700' },
};

function VenueTag({ venue }: { venue: TradingVenue }) {
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${VENUE_TAG[venue].tone}`}>
      {VENUE_TAG[venue].label}
    </span>
  );
}

// Build a PositionCard VM from an attributed org row + the live mark, so the
// card / compact / mini views render identically to the Positions page. Read-
// only here (editable: false) — the admin/manager doesn't close traders'
// positions from this aggregate view.
function orgPositionVM(
  p: OrgPositionRow,
  nowCents: number | null,
  polyFeeSchedule?: PolyFeeSchedule | null,
): PositionCardVM {
  const lifetime = reconciledOrgLifetimeSource(p);
  if (p.venue === 'polymarket') {
    return buildPolyPositionVM({
      ticker: p.ticker,
      side: p.side,
      netCount: p.qty,
      avgEntryPriceCents: p.avgCents,
      lastFillAt: p.lastFillAt,
      costBasisCents: p.costBasisCents,
      realizedPnlCents: 0,
      feesPaidCents: p.feesPaidCents,
      openFeesCents: p.openFeesCents,
      splitCount: p.splitCount, splitConflictCount: p.splitConflictCount, splitFeeUnknownCount: p.splitFeeUnknownCount,
      ...lifetime,
    }, null, nowCents, polyFeeSchedule);
  }
  const { title, subtitle, team, titleTeam, marketType } = titleParts(p.ticker, p.side);
  // Open-share fees only (never the bucket total — sold-share fees belong to
  // realized). Unknown allocation → P&L withheld below, not priced as $0-fee.
  const openFeesKnown = p.openFeesCents != null;
  const entryCostCents = p.costBasisCents + (p.openFeesCents ?? 0);
  const takerRate = takerRateForTicker(p.ticker);
  const closingFeeCents = nowCents != null && takerRate != null
    ? estimateAggregateFeeCents({ priceCents: nowCents, count: p.qty, feeRate: takerRate })
    : 0;
  const cashOutCents = nowCents != null ? nowCents * p.qty - closingFeeCents : null;
  const pnlCents = cashOutCents != null && openFeesKnown ? cashOutCents - entryCostCents : null;
  return {
    key: `${p.userId}-${p.ticker}-${p.side}`,
    venue: p.venue,
    ticker: p.ticker,
    title,
    subtitle,
    titleTeam,
    marketType,
    eventStartLabel: null,
    lastFillAt: p.lastFillAt,
    side: p.side,
    team,
    qty: p.qty,
    restingCloseQty: 0,
    // Full-position anchor (#91/#175): render only when the manager rollup's
    // authoritative lifetime totals reconcile exactly to the held remainder.
    // Remote/residual rows carry no such evidence and stay dashed.
    ...fullPositionAnchor(lifetime),
    avgCents: p.avgCents,
    avgOdds: americanOdds(p.qty > 0 ? p.costBasisCents / p.qty : p.avgCents),
    feesCents: p.feesPaidCents ?? null,
    costDollars: entryCostCents / 100,
    nowCents,
    nowOdds: nowCents != null ? americanOdds(Math.round(nowCents)) : null,
    pnlDollars: pnlCents != null ? pnlCents / 100 : null,
    cashOutDollars: cashOutCents != null ? cashOutCents / 100 : null,
    ifWinDollars: (p.qty * 100 - entryCostCents) / 100,
    grossPayoutDollars: p.qty,
    impliedOdds: p.qty > 0 ? americanOdds(p.costBasisCents / p.qty) : null,
    editable: false,
  };
}

/** #1227 — a Polymarket US holding as a row of the org's Open Positions,
 *  through the Tracker's builder (#1226 / #1199): exact size, recorded basis and
 *  fees; absent evidence remains "—". Read-only: no Close. The key
 *  carries the owner (the scope is per trader and account), so two traders
 *  holding the same market on a shared account stay two rows. */
function orgUsPositionVM({ row, accountId, title }: OrgUsPosition, market: UsPositionMarket | null, nowMs: number): PositionCardVM {
  const vm = buildUsPositionVM({ ...row, target: { ...row.target, marketTitle: title } },
    { accountId, accountLabel: null, eventStartLabel: null, sport: null, closeState: null, busy: false, market, nowMs });
  return { ...vm, key: `us|${usPositionKey(row)}` };
}

function UsPositionTableRow({ p, vm, masked }: { p: OrgUsPosition; vm: PositionCardVM; masked: boolean }) {
  const facts = vm.us!;
  return (
    <tr className="border-border/40 border-b" data-testid="org-detail-us-position" data-position-key={vm.key}>
      <td className="text-foreground px-4 py-2 text-xs" title={facts.marketSlug}>{facts.marketLabel}</td>
      <td className="px-3 py-2"><VenueTag venue="polymarket_us" /></td>
      <td className="px-3 py-2"><OwnerTag owner={p.owner} former={p.former} /></td>
      <td className="px-3 py-2 text-center"><SideBadge side={vm.side} betTeam={null} /></td>
      {/* nowrap: the number stays on one line here (cards may break it); the label wraps at its spaces and hyphens. */}
      <td className="text-foreground whitespace-nowrap px-3 py-2 text-right font-mono text-xs"><PositionQty vm={vm} /></td>
      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">{fmtCents(vm.avgCents)}</td>
      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs" title={vm.lastFillAt ?? undefined}>{fmtLastFillAt(vm.lastFillAt)}</td>
      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">{fmtCents(vm.nowCents)}</td>
      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">{fmtUsd(vm.entryBasisDollars ?? null)}</td>
      <td className={`px-3 py-2 text-right font-mono text-xs ${masked || vm.pnlDollars == null ? 'text-muted-foreground' : vm.pnlDollars >= 0 ? 'text-positive' : 'text-negative'}`}>
        {masked ? '•••' : fmtUsdSigned(vm.pnlDollars)}
      </td>
    </tr>
  );
}

/** The Trades table lists the newest this many of the merged K/P + US list. */
const TRADES_SHOWN = 80;

/** A Trades row: a K/P fill, or a Polymarket US fill (#1227). */
type OrgTradeRow = { kind: 'kp'; f: OrgFillRow } | { kind: 'us'; f: OrgUsFill };

/** #1227 — the org's K/P and US trades in one newest-first list. Both reads
 *  arrive newest first (K/P: the relay's latest 500; US: the org's latest 20,
 *  untimed last), so each keeps its own order and the two interleave by time.
 *  A US fill without a time, or with a disputed one, follows every timed row;
 *  at equal times K/P comes first. */
function mergeTrades(kp: readonly OrgFillRow[], us: readonly OrgUsFill[]): OrgTradeRow[] {
  const usMs = (f: OrgUsFill) => (f.executedAt && !f.disputed.includes('executedAt') ? Date.parse(f.executedAt) : Number.NaN);
  const out: OrgTradeRow[] = [];
  let i = 0;
  let j = 0;
  while (i < kp.length && j < us.length) {
    const u = usMs(us[j]);
    // A K/P fill keeps its place unless it is known to be older (a bad K/P time never lets US rows jump it).
    if (Number.isNaN(u) || !(Date.parse(kp[i].executedAt) < u)) out.push({ kind: 'kp', f: kp[i++] });
    else out.push({ kind: 'us', f: us[j++] });
  }
  while (i < kp.length) out.push({ kind: 'kp', f: kp[i++] });
  while (j < us.length) out.push({ kind: 'us', f: us[j++] });
  return out;
}

/** A US fill two members' intents claim is disputed: shown, never pinned on one of them (#1023). */
function UsFillOwner({ owners }: { owners: OwnerAttribution[] }) {
  if (owners.length === 1) return <OwnerTag owner={owners[0]} />;
  return (
    <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-400 light:text-amber-700"
      title={`Claimed by more than one trader: ${owners.map((o) => o.label).join(', ')}`}>
      disputed
    </span>
  );
}

/** A Polymarket US fill as a Trades row: the K/P columns, the US venue tag,
 *  the label in the K/P shape (slug in the hover), exact quantity and price;
 *  a field the venue disputes is "—" with the reason in its hover. Fees use
 *  saved execution commission only; missing or conflicting evidence stays unknown. */
function UsTradeTableRow({ f }: { f: OrgUsFill }) {
  const label = f.marketSlug === null ? 'market disputed' : usMarketLabel({ marketSlug: f.marketSlug, marketTitle: f.marketTitle }).label;
  return (
    <tr className="border-border/40 border-b" data-testid="org-detail-us-trade">
      <td className="text-foreground px-4 py-2 text-xs [overflow-wrap:anywhere]" title={f.marketSlug ?? 'market disputed'}>
        {f.evidenceState === 'conflicting' && <span className="mr-1 text-amber-400 light:text-amber-700" title={US_CONFLICT_TITLE}>⚠</span>}
        {label}
      </td>
      <td className="px-3 py-2"><VenueTag venue="polymarket_us" /></td>
      <td className="px-3 py-2"><UsFillOwner owners={f.owners} /></td>
      <td className="px-3 py-2 text-center">{f.outcome === null ? dash('side disputed') : <SideBadge side={f.outcome} betTeam={null} />}</td>
      <td className="px-3 py-2 text-center">
        {f.action === null ? dash('action disputed') : (
          <span className={f.action === 'buy' ? 'text-emerald-400' : 'text-amber-400 light:text-amber-700'}>{f.action.toUpperCase()}</span>
        )}
      </td>
      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
        {f.quantity === null ? dash('quantity disputed') : formatUsDecimal(f.quantity)}
      </td>
      <td className="text-foreground px-3 py-2 text-right font-mono text-xs"><UsFillPrice fill={f} /></td>
      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
        {f.feeUsd == null ? dash(f.evidenceState === 'conflicting' ? 'Recorded execution fee unavailable: conflicting evidence' : 'Recorded execution fee unavailable')
          : <span title="Recorded execution fee">{formatUsUsd(f.feeUsd)}</span>}
      </td>
      <td className="text-muted-foreground px-4 py-2 text-right text-[11px]"><UsFillTime fill={f} /></td>
    </tr>
  );
}

export function OrgDetail({ orgId, orgName, onBack, onTraderClick }: OrgDetailProps) {
  const [balanceCents, setBalanceCents] = useState<number | null>(null);
  // Attributed rows (per-trader ledgers) and residual unattributed rows (venue
  // snapshot minus attributed) land from different fetches; keeping them in
  // separate state and merging at render removes any landing-order dependency.
  // #1386: a failed core read is unavailable, never [] (and never retained:
  // see the core landing); members ride the same read.
  const [positionsPanel, setPositionsPanel] = useState<OrgReadPanel<OrgPositionRow>>(unreadOrgPanel);
  const positions = positionsPanel.rows;
  // Sibling-instance rows (cross-instance ledger sync) sit between the two:
  // attributed, but not to a local trader. They subtract from the residual
  // exactly like local attribution does.
  const [remotes, setRemotes] = useState<OrgPositionRow[]>([]);
  const [residuals, setResiduals] = useState<OrgPositionRow[]>([]);
  // #1394: whether the account snapshot (the source of remotes + residuals) is
  // known. Unknown is never an empty account: totals withhold and the panel says so.
  const [accountHoldings, setAccountHoldings] = useState<OrgAccountHoldings>('pending');
  const [syncSiblings, setSyncSiblings] = useState<SiblingSyncStatus[]>([]);
  const [syncFetchedAt, setSyncFetchedAt] = useState<string | null>(null);
  // #1386: the orders panel keeps what its last read said, so a failed read
  // is unavailable (or the last confirmed rows), never an empty list.
  const [ordersPanel, setOrdersPanel] = useState<OrgReadPanel<OrgOrderRow>>(unreadOrgPanel);
  const orders = ordersPanel.rows;
  // #1386: the trades panel's K/P tape, held the same way as the orders panel.
  const [fillsPanel, setFillsPanel] = useState<OrgReadPanel<OrgFillRow>>(unreadOrgPanel);
  const fills = fillsPanel.rows;
  const [members, setMembers] = useState<OrgTraderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [fillsLoading, setFillsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Monotonic fetch generation: an org switch or refresh mid-flight must not
  // let a stale response write state for the wrong org.
  const fetchGen = useRef(0);
  // The org the K/P rows on screen were read for (#1227).
  const fetchedOrg = useRef<string | null>(null);
  const [sportFilter, setSportFilter] = useState<SportFilter>(() => new Map());
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilterKey>('all');
  // #1023: the Refresh button re-reads the US rows too (#1227: they are the page's own).
  const [usRefresh, setUsRefresh] = useState(0);

  // Live mark for the Now / unrealized columns. version drives re-render on
  // every hydra tick; getMarketPrice returns the YES mid (cents).
  const marketVersion = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  // Consumer feed filters (feed-filters-per-view): the whole org's open
  // book by identity (positions, sibling-instance remotes, residuals,
  // resting orders); fills are history. Undeclared until positions land.
  const orgFeedIds = useMemo(
    () => (loading ? null : [
      ...positions.map((p) => p.ticker),
      ...remotes.map((p) => p.ticker),
      ...residuals.map((p) => p.ticker),
      ...orders.map((o) => o.ticker),
    ]),
    [loading, positions, remotes, residuals, orders],
  );
  useTickerFeedNeeds('org-detail', orgFeedIds, { fold: 'view' });
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const toggleShowPnl = useDisplaySettings((s) => s.toggleShowPnl);
  const positionViewMode = useDisplaySettings((s) => s.positionViewMode);
  const setPositionViewMode = useDisplaySettings((s) => s.setPositionViewMode);

  const orgQueryParam = orgId ? `?orgId=${orgId}` : '';

  const fetchAll = useCallback(async () => {
    const gen = ++fetchGen.current;
    const fresh = () => fetchGen.current === gen;
    setLoading(true);
    setOrdersLoading(true);
    setFillsLoading(true);
    setError(null);
    // Residuals belong to a (generation, account-fetch) pair; without this
    // reset an org switch could merge org A's residual rows into org B's view
    // until B's account fetch lands. Remote rows ride the same fetch pair.
    setResiduals([]);
    setRemotes([]);
    setAccountHoldings('pending');
    // #1227: an org switch also drops the last org's attributed positions and
    // fills. The lists no longer wait behind a spinner once the new org's US
    // rows land, so A's K/P rows must not sit beside B's US rows. A Refresh
    // (same org) keeps them on screen until the new read lands, as before.
    // #1386: orders and members too — a failed orders read keeps the last
    // confirmed rows, which must never be another org's, and none of the last
    // org's rows or members may show while this org's reads are out.
    if (fetchedOrg.current !== orgQueryParam) {
      fetchedOrg.current = orgQueryParam;
      setPositionsPanel(unreadOrgPanel());
      setMembers([]);
      // The last org's balance must not show under this one while its account read is out.
      setBalanceCents(null);
      setFillsPanel(unreadOrgPanel());
      setOrdersPanel(unreadOrgPanel());
    }

    // Partial-failure signal, priority-ranked (core 0 > orders 1 > fills 2 >
    // balance 3) so a fast trivial failure can't shadow a consequential one —
    // and an empty table never reads as "nothing on Kalshi" when really a
    // fetch failed. Rank state is per-generation (closure-local).
    let errorRank = Number.POSITIVE_INFINITY;
    const softFail = (rank: number, msg: string) => {
      if (rank < errorRank) {
        errorRank = rank;
        setError(msg);
      }
    };

    // Four independent fetches, four independent landings. The shell gates
    // ONLY on positions-by-trader; orders, fills, and the account snapshot
    // each hydrate their own panel when ready. Never gate the page on its
    // slowest fetch (fills-by-owner walks the org fill history and is the
    // slow one under load).
    const posDataP = apiFetch(`/api/manager/org/positions-by-trader${orgQueryParam}`)
      .then(async (res) => ({
        ok: res.ok,
        data: (await res.json().catch(() => null)) as { rows?: OrgTraderRow[]; error?: string } | null,
      }))
      .catch(() => ({ ok: false, data: null }));

    // Attributed rows resolve once, shared by the shell and the residual calc.
    const attributedRowsP: Promise<OrgPositionRow[] | null> = posDataP.then(({ ok, data }) => {
      // #1386: only a rows list of objects is a read; anything else is unavailable.
      if (!ok || !Array.isArray(data?.rows) || !data.rows.every((r) => r !== null && typeof r === 'object' && !Array.isArray(r))) return null;
      // Positions: flatten each trader's openPositions, tagging the owner.
      return data.rows.flatMap((r) =>
        (r.openPositions ?? []).map((p) => ({
          userId: r.userId,
          owner: resolveOwnerAttribution({
            ownerKind: 'trader',
            ownerUserId: r.userId,
            ownerUsername: r.username,
          }),
          venue: p.venue ?? 'kalshi',
          ticker: p.ticker,
          side: p.side,
          qty: p.netCount,
          avgCents: p.avgEntryPriceCents,
          lastFillAt: p.lastFillAt ?? null,
          // Exact ledger cost basis when present, else avg × contracts.
          costBasisCents: p.costBasisCents ?? Math.round(p.avgEntryPriceCents * p.netCount),
          feesPaidCents: p.feesPaidCents,
          openFeesCents: p.openFeesCents,
      splitCount: p.splitCount, splitConflictCount: p.splitConflictCount, splitFeeUnknownCount: p.splitFeeUnknownCount,
          totalBoughtCount: p.totalBoughtCount,
          totalSoldCount: p.totalSoldCount,
          totalBuyCostCents: p.totalBuyCostCents,
          sport: p.venue === 'polymarket'
            ? (describePolyMarketCached(p.ticker)?.sport ?? null)
            : sportFromTicker(p.ticker),
        })),
      );
    });

    // Core panel: traders + attributed positions.
    void (async () => {
      const [{ data }, posRows] = await Promise.all([posDataP, attributedRowsP]);
      if (!fresh()) return;
      if (posRows == null) {
        softFail(
          0,
          typeof data?.error === 'string'
            ? data.error
            : 'Could not load trader attribution — the org may not have a Kalshi credential configured; other panels may be incomplete.',
        );
        // #1386: unavailable, never "No open positions". Not retained through a
        // failed Refresh, unlike orders and fills: the residual and sibling rows
        // are rebuilt from this read on every fetch, so a kept list would drop
        // them (an org whose holdings are all unattributed would read empty),
        // and the members' figures would mix stale K/P with live US and marks.
        setPositionsPanel(unavailableOrgPanel());
        setResiduals([]);
        setMembers([]);
        return;
      }
      setMembers(data?.rows ?? []);
      // Residual (unattributed) rows land separately from the account fetch.
      setPositionsPanel({ rows: posRows, confirmed: true, disclosure: 'none' });
    })()
      .catch(() => {
        if (!fresh()) return;
        setPositionsPanel(unavailableOrgPanel());
        setMembers([]);
        softFail(0, 'Could not load org data.');
      })
      .finally(() => {
        if (fresh()) setLoading(false);
      });

    // Sibling-instance positions resolve alongside the account snapshot; a
    // failure (or a non-master relay with no mirror) degrades cleanly to the
    // old residual-only behavior.
    const remotePosP: Promise<RemotePositionWire[] | null> = apiFetch(
      `/api/manager/org/remote-positions${orgQueryParam}`,
    )
      .then(async (res) =>
        res.ok
          ? (((await res.json().catch(() => null)) as { positions?: RemotePositionWire[] } | null)
              ?.positions ?? [])
          : null,
      )
      .catch(() => null);

    // Account panel: balance now, remote + residual position rows once the
    // snapshot, the attributed rows, and the remote fold are all in (they
    // fetch concurrently).
    void (async () => {
      const acctRes = await apiFetch(`/api/manager/org/account${orgQueryParam}`).catch(() => null);
      const acctData = acctRes
        ? ((await acctRes.json().catch(() => null)) as
            | { balance?: Record<string, unknown>; positions?: unknown[]; error?: string }
            | null)
        : null;
      const posRows = await attributedRowsP;
      const remoteWire = await remotePosP;
      if (!fresh()) return;

      const bal = acctData?.balance ?? null;
      setBalanceCents(bal ? readKalshiReportedBalanceCents(bal) : null);
      if (!acctRes?.ok) softFail(3, 'Balance did not load.');
      // #1394: a failed, unreadable or partial snapshot is unknown, never an
      // empty account — treating it as [] erased every unattributed and sibling
      // row while the totals still read complete (an org holding only
      // unattributed positions showed "No open positions", 0, $0). No rows are
      // kept through a failed Refresh: residuals are relative to THIS
      // generation's attributed read, so the last snapshot's rows beside fresh
      // attribution could double-count a contract a ledger has since claimed.
      const venuePositions = decodeOrgAccountPositions(acctRes?.ok === true, acctData);
      setAccountHoldings(venuePositions ? 'known' : 'unknown');
      if (posRows == null || venuePositions == null) return; // nothing to attribute against

      const venueKeys = new Set(venuePositions.map((p) => `${p.ticker}::${p.side}`));

      // Remote rows render only where they explain part of a LIVE venue
      // position — the remote fold spans full fill history, so settled
      // markets (which the venue snapshot no longer lists) drop out here.
      const remoteRows: OrgPositionRow[] = (remoteWire ?? [])
        .filter((r) => venueKeys.has(`${r.ticker}::${r.side}`))
        .map((r) => ({
          userId: `remote:${r.surface}:${r.remoteUserId}`,
          owner: resolveOwnerAttribution({
            ownerKind: 'remote',
            remoteSurface: r.surface,
            remoteUserId: r.remoteUserId,
            remoteUsername: r.remoteUsername,
          }),
          venue: 'kalshi' as const,
          ticker: r.ticker,
          side: r.side,
          qty: r.qty,
          avgCents: r.avgCents,
          lastFillAt: r.lastFillAt,
          costBasisCents: r.costBasisCents,
          feesPaidCents: r.feesPaidCents ?? undefined,
          sport: sportFromTicker(r.ticker),
        }));
      setRemotes(remoteRows);

      // Residual = venue − locally attributed − remote-attributed. What's
      // left is genuinely unattributed (external automation on the shared
      // keys, or a sibling not yet synced).
      const attributedQty = new Map<string, number>();
      for (const p of [...posRows, ...remoteRows]) {
        const key = `${p.ticker}::${p.side}`;
        attributedQty.set(key, (attributedQty.get(key) ?? 0) + p.qty);
      }
      const residualRows: OrgPositionRow[] = venuePositions.flatMap((p) => {
        const key = `${p.ticker}::${p.side}`;
        const residual = p.position - (attributedQty.get(key) ?? 0);
        if (residual <= 0.0001) return [];
        return [{
          userId: 'unattributed',
          owner: UNATTRIBUTED_OWNER,
          venue: 'kalshi' as const,
          ticker: p.ticker,
          side: p.side,
          qty: residual,
          avgCents: p.averagePrice,
          lastFillAt: p.lastFillAt ?? null,
          costBasisCents: p.costBasisCents ?? Math.round(p.averagePrice * residual),
          sport: sportFromTicker(p.ticker),
        }];
      });
      setResiduals(residualRows);
    })().catch(() => {
      if (!fresh()) return;
      setAccountHoldings('unknown');
      setRemotes([]);
      setResiduals([]);
      softFail(3, 'Account snapshot did not load — balance and unattributed positions may be missing.');
    });

    // Sync-health strip — independent, cosmetic-only landing. Empty siblings
    // (every non-master instance) renders nothing.
    void apiFetch(`/api/manager/org/attribution-sync-status${orgQueryParam}`)
      .then(async (res) => {
        if (!res.ok || !fresh()) return;
        const data = (await res.json().catch(() => null)) as
          | { siblings?: SiblingSyncStatus[]; fetchedAt?: string }
          | null;
        if (!fresh()) return;
        setSyncSiblings(data?.siblings ?? []);
        setSyncFetchedAt(data?.fetchedAt ?? null);
      })
      .catch(() => {});

    // Orders panel — independent landing.
    void (async () => {
      const ordRes = await apiFetch(`/api/manager/org/orders-by-trader${orgQueryParam}`).catch(() => null);
      const ordData = ordRes
        ? ((await ordRes.json().catch(() => null)) as
            | { orders?: Array<Record<string, unknown>>; error?: string }
            | null)
        : null;
      if (!fresh()) return;

      // Orders: parse the raw Kalshi orders with the canonical strict helper
      // (#1386: a non-2xx or unparseable read is null — unavailable, never []),
      // then re-attach the owner tag the relay added (parseOrders drops extras).
      const parsed = ordRes?.ok ? parseOrdersSnapshot(ordData) : null;
      if (!parsed) {
        setOrdersPanel((prev) => nextOrgPanel(prev, null));
        softFail(1, 'Orders did not load — the order list may be incomplete.');
        return;
      }
      const ownerByOrderId = new Map<string, OwnerAttribution>();
      for (const o of ordData?.orders ?? []) {
        const id = o.order_id;
        if (typeof id === 'string') {
          ownerByOrderId.set(id, resolveOwnerAttribution(o));
        }
      }
      const ordRows: OrgOrderRow[] = parsed.map((o) => {
        const owner = ownerByOrderId.get(o.orderId) ?? UNATTRIBUTED_OWNER;
        const sidePrice =
          o.side === 'no'
            ? (o.noPrice ?? (o.yesPrice != null ? 100 - o.yesPrice : null))
            : (o.yesPrice ?? (o.noPrice != null ? 100 - o.noPrice : null));
        return {
          orderId: o.orderId,
          owner,
          ticker: o.ticker,
          side: o.side,
          qty: o.remainingCount,
          priceCents: sidePrice,
          sport: sportFromTicker(o.ticker),
        };
      });
      setOrdersPanel((prev) => nextOrgPanel(prev, ordRows));
    })()
      .catch(() => {
        if (!fresh()) return;
        setOrdersPanel((prev) => nextOrgPanel(prev, null));
        softFail(1, 'Orders did not load — the order list may be incomplete.');
      })
      .finally(() => {
        if (fresh()) setOrdersLoading(false);
      });

    // Trades panel — independent landing (the heavy fetch under load).
    void (async () => {
      const fillsRes = await apiFetch(`/api/manager/org/fills-by-owner${orgQueryParam}`).catch(() => null);
      const fillsData = fillsRes
        ? ((await fillsRes.json().catch(() => null)) as
            | { fills?: Array<Record<string, unknown>>; error?: string }
            | null)
        : null;
      if (!fresh()) return;

      // #1386: a failed or unparseable read is unavailable, never [].
      const rawFills = decodeOrgFillRows(fillsRes?.ok === true, fillsData);
      if (!rawFills) {
        setFillsPanel((prev) => nextOrgPanel(prev, null));
        softFail(2, 'Trades did not load — the fill tape may be incomplete.');
        return;
      }
      const fillRows: OrgFillRow[] = rawFills.map((f) => {
        const owner = resolveOwnerAttribution(f);
        const side = f.side === 'no' ? 'no' : 'yes';
        const ticker = String(f.ticker ?? '');
        return {
          fillId: String(f.fill_id ?? f.trade_id ?? ''),
          orderId: typeof f.order_id === 'string' ? f.order_id : null,
          owner,
          venue: f.venue === 'polymarket' ? 'polymarket' : 'kalshi',
          ticker,
          side,
          action: f.action === 'sell' ? 'sell' : 'buy',
          qty: Number(f.count ?? 0),
          priceCents: typeof f.price === 'number' ? f.price : null,
          feeCents: typeof f.fee_cents === 'number' ? f.fee_cents : null,
          executedAt: String(f.created_time ?? ''),
          sport: f.venue === 'polymarket'
            ? (describePolyMarketCached(ticker)?.sport ?? null)
            : sportFromTicker(ticker),
        };
      });
      setFillsPanel((prev) => nextOrgPanel(prev, fillRows));
    })()
      .catch(() => {
        if (!fresh()) return;
        setFillsPanel((prev) => nextOrgPanel(prev, null));
        softFail(2, 'Trades did not load — the fill tape may be incomplete.');
      })
      .finally(() => {
        if (fresh()) setFillsLoading(false);
      });
  }, [orgQueryParam]);
  // One refresh for the Refresh button and the US rows' Retry (#1023).
  const refreshAll = () => { fetchAll(); setUsRefresh((n) => n + 1); };

  useEffect(() => {
    fetchAll();
    // Invalidate in-flight handlers on unmount/org-switch so no stale landing
    // writes state afterward.
    return () => {
      fetchGen.current += 1;
    };
  }, [fetchAll]);

  // #1227: the org's Polymarket US rows, read once for the whole page.
  const us = useUsOrgRows({ orgQueryParam, ownerFilter, sportFilter, refreshKey: usRefresh, economics: true });
  const { current: usCurrent, marks: usMarks, nowMs: usNowMs,
    positions: usPositionRows } = us;
  const institutionalDesk = useInstitutionalDeskOrders(usCurrent?.institutionalAccounts, orgQueryParam, usRefresh);
  const institutionalRows = ownerMatchesFilter(UNATTRIBUTED_OWNER, ownerFilter)
    ? institutionalDesk.data?.snapshot?.orders ?? [] : [];
  const institutionalOrderStatus = useInstitutionalOrderStatus(institutionalDesk, institutionalRows, orgQueryParam);
  const usMemberEconomics = useMemo(() => {
    return new Map(members.map(member => [member.userId, usOrgMemberEconomics(usCurrent, member.userId, usMarks, usNowMs)]));
  }, [members, usCurrent, usMarks, usNowMs]);

  useEffect(() => {
    // An owner key is only meaningful inside the org that supplied it.
    setOwnerFilter('all');
  }, [orgQueryParam]);

  // Side-native current price for a ticker (cents), from the live hydra mark.
  const nowCentsFor = useCallback(
    (venue: 'kalshi' | 'polymarket', ticker: string, side: 'yes' | 'no'): number | null => {
      if (venue === 'polymarket') return polyPositionLiveData(ticker, side).nowCents;
      const yesMid = getMarketPrice(ticker);
      if (yesMid == null) return null;
      return side === 'yes' ? yesMid : 100 - yesMid;
    },
    // marketVersion forces re-eval as prices tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getMarketPrice, marketVersion],
  );

  // Attributed + remote + residual rows, merged at render (landing-order
  // independent).
  const allPositions = useMemo(
    () => [...positions, ...remotes, ...residuals],
    [positions, remotes, residuals],
  );
  const ownerOptions = useMemo(() => {
    const options = new Map<OwnerFilterKey, string>([['all', 'All owners']]);
    if (institutionalDesk.data?.snapshot?.orders.length) options.set('unattributed', 'Unattributed');
    for (const member of members) {
      options.set(`trader:${member.userId}`, member.username);
    }
    for (const owner of [
      ...allPositions.map((row) => row.owner),
      ...orders.map((row) => row.owner),
      ...fills.map((row) => row.owner),
    ]) {
      if (owner.kind === 'unattributed') {
        options.set('unattributed', 'Unattributed');
      } else {
        options.set(owner.filterKey, owner.label ?? owner.filterKey);
      }
    }
    // #1227: US owners too, a former member holding only US among them. They
    // only add: a name the K/P rows already gave is never relabelled.
    for (const owner of [
      ...(usCurrent?.positions?.rows ?? []).map((row) => row.owner),
      ...(usCurrent?.fills?.rows ?? []).flatMap((row) => row.owners),
    ]) {
      if (options.has(owner.filterKey)) continue;
      options.set(owner.filterKey, owner.kind === 'unattributed' ? 'Unattributed' : owner.label ?? owner.filterKey);
    }
    return [...options].map(([key, label]) => ({ key, label }));
  }, [allPositions, fills, members, orders, usCurrent, institutionalDesk.data]);

  useEffect(() => {
    if (!ownerOptions.some((option) => option.key === ownerFilter)) {
      setOwnerFilter('all');
    }
  }, [ownerFilter, ownerOptions]);

  const polyFeeConditionIds = useMemo(
    () => allPositions.filter((position) => position.venue === 'polymarket').map((position) => position.ticker),
    [allPositions],
  );
  const { schedules: polyFeeSchedules } = usePolyFeeSchedules(polyFeeConditionIds);
  const visiblePositions = useMemo(
    () => allPositions.filter(
      (p) => sportMatchesFilter(p.sport, sportFilter) && ownerMatchesFilter(p.owner, ownerFilter),
    ),
    [allPositions, ownerFilter, sportFilter],
  );
  const visibleOrders = useMemo(
    () => orders.filter(
      (o) => sportMatchesFilter(o.sport, sportFilter) && ownerMatchesFilter(o.owner, ownerFilter),
    ),
    [orders, ownerFilter, sportFilter],
  );
  const visibleFills = useMemo(
    () => fills.filter(
      (f) => sportMatchesFilter(f.sport, sportFilter) && ownerMatchesFilter(f.owner, ownerFilter),
    ),
    [fills, ownerFilter, sportFilter],
  );

  const selectedMember = useMemo(() => {
    if (!ownerFilter.startsWith('trader:')) return null;
    const userId = ownerFilter.slice('trader:'.length);
    return members.find((member) => member.userId === userId) ?? null;
  }, [members, ownerFilter]);

  // This is deliberately the SAME calculation used by the per-member rail and
  // OrgTradersTable. A filtered headline cannot drift into a second P&L recipe.
  const selectedMemberPnl = useMemo(() => {
    void marketVersion;
    if (!selectedMember) return null;
    const unrealized = computeOrgTraderUnrealized(
      selectedMember,
      getMarketPrice,
      undefined,
      (ticker) => polyFeeSchedules.get(ticker),
    );
    const realizedNet = selectedMember.realizedPnlNetCents ?? null;
    const usSummary = usMemberEconomics.get(selectedMember.userId)!;
    const combinedRealized = addKnown(realizedNet, usSummary.realizedNetCents);
    const combinedUnrealized = addKnown(unrealized.valueCents, usSummary.unrealizedCents);
    return {
      valueCents: addKnown(combinedRealized, combinedUnrealized),
      feesCents: addKnown(selectedMember.realizedFeesCents, usSummary.realizedFeesCents),
      unrealized,
    };
  }, [getMarketPrice, marketVersion, polyFeeSchedules, selectedMember, usMemberEconomics]);

  // #1394: the account snapshot completes every view but a local trader's or bot's.
  const accountComplete = orgAccountOwnerComplete(accountHoldings, ownerFilter);
  // Org aggregates over the visible rows.
  const agg = useMemo(() => {
    let notionalCents = 0;
    let unrealizedCents = 0;
    let pricedPositions = 0;
    for (const p of visiblePositions) {
      notionalCents += p.costBasisCents;
      const now = nowCentsFor(p.venue, p.ticker, p.side);
      if (now != null) {
        const vm = orgPositionVM(p, now, polyFeeSchedules.get(p.ticker));
        if (vm.pnlDollars != null) {
          pricedPositions++;
          unrealizedCents += vm.pnlDollars * 100;
        }
      }
    }
    const usTotals = usOrgPositionEconomics(usPositionRows.rows,
      usOrgOwnerComplete(usCurrent, ownerFilter), usMarks, usNowMs);
    const totalPositions = visiblePositions.length + usTotals.totalPositions;
    pricedPositions += usTotals.pricedPositions;
    // #1386: without a confirmed current K/P read the K/P part is unknown, never 0.
    // #1394: nor without the account snapshot, where this view can hold its rows.
    const kpKnown = positionsPanel.confirmed && positionsPanel.disclosure === 'none' && accountComplete;
    return {
      notionalCents: kpKnown ? addKnown(notionalCents, usTotals.notionalCents) : null,
      unrealizedCents: addKnown(unrealizedCents, usTotals.unrealizedCents),
      pricedPositions,
      totalPositions,
      kpKnown,
      unrealizedComplete: kpKnown && usTotals.complete && pricedPositions === totalPositions,
    };
  }, [visiblePositions, nowCentsFor, polyFeeSchedules, usCurrent, usPositionRows, usMarks, usNowMs, ownerFilter, positionsPanel, accountComplete]);

  const usPositions = useMemo(() => {
    return usPositionRows.rows.map((p) => ({ p, vm: orgUsPositionVM(p, usMarks.getMarket(p.row.target.marketSlug), usNowMs) }));
  }, [usPositionRows, usMarks, usNowMs]);
  const usCounted = us.current?.positions != null;
  const economicsVenues = us.current?.accounts === 'absent' ? 'K+P' : 'K+P+US';
  const shownPositionCount = visiblePositions.length + usPositions.length;
  // The list's spinner stands while the K/P core read is out and no US row is
  // shown: US rows that have answered are never held behind it (#1060: relay
  // reads have no timeout); "Reading Kalshi and Polymarket positions…" says
  // what is still out. An org without US keeps the spinner exactly as before.
  const positionsSpinner = loading && usPositions.length === 0;
  // What the US read could not give, said once per list: the section's four
  // states (#1023) — checking, failed (with Retry), no US, US.
  const usReadLine = (noun: 'positions' | 'trades', read: unknown): ReactNode => (!us.current ? (
    <span className="text-muted-foreground" role="status" aria-busy="true">Checking Polymarket US…</span>
  ) : us.current.accounts === 'failed' ? (
    <span className="text-amber-500 light:text-amber-700">
      {usReadStatus(noun, 'unavailable').text}<UsRetry onRetry={refreshAll} busy={us.reReading} />
    </span>
  ) : us.current.accounts === 'present' && read === null ? (
    <span className="text-amber-500 light:text-amber-700">
      {usReadStatus(noun, 'unavailable').text}<UsRetry onRetry={refreshAll} busy={us.reReading} />
    </span>
  ) : null);
  const usPositionsStatus = usReadLine('positions', us.current?.positions);
  // The US line also shows under the spinner: a K/P read still out never hides it.
  const usPositionsLine = (shownPositionCount > 0 || positionsSpinner) && usPositionsStatus !== null;
  const usPositionsReading = loading && !positionsSpinner;
  // #1386: the positions panel names its own failed core read, as orders and trades do.
  const kPositionsNote = positionsPanel.disclosure === 'unavailable' ? 'Kalshi and Polymarket positions did not load.' : null;
  const kPositionsNoteInline = shownPositionCount === 0 && usPositionsStatus === null && positionsPanel.disclosure === 'unavailable';
  // The last attempt's failure line steps aside while the panel re-reads.
  const kPositionsNoteBelow = kPositionsNote !== null && !loading && !kPositionsNoteInline;
  // #1394: the account snapshot's rows (unattributed, sibling-instance) did not
  // load. Said only where they could be listed and the core read stands (a
  // failed core read already says the positions did not load).
  const kAccountNote = accountHoldings === 'unknown' && !accountComplete && positionsPanel.disclosure === 'none'
    ? 'Unattributed and sibling-instance Kalshi holdings did not load — this list may be incomplete.' : null;
  const kAccountNoteInline = kAccountNote !== null && shownPositionCount === 0 && usPositionsStatus === null;
  const kAccountNoteBelow = kAccountNote !== null && !loading && !kAccountNoteInline;
  // While the snapshot is still out (it lands after /remote-positions too, and
  // relay reads have no timeout) the withheld tiles say why, and an empty list
  // never reads "No open positions" ahead of it.
  const kAccountReading = accountHoldings === 'pending' && !accountComplete && !loading
    && positionsPanel.disclosure === 'none' ? 'Reading the account snapshot…' : null;
  const kAccountReadingInline = kAccountReading !== null && shownPositionCount === 0 && usPositionsStatus === null;
  const kAccountReadingBelow = kAccountReading !== null && !kAccountReadingInline;
  const usPositionNotes = kPositionsNoteBelow || kAccountNoteBelow || kAccountReadingBelow || usPositionsLine || usPositionsReading
    || us.current?.positions?.complete === false || us.positions.hidden + us.positions.pending > 0;

  // #1227: US trades are rows of Trades. The US read is the org's latest 20
  // (the relay's cap), so the list and the tile say so once US is in them.
  const trades = useMemo(() => mergeTrades(visibleFills, us.fills.rows), [visibleFills, us.fills.rows]);
  const usTradesCounted = us.current?.fills != null;
  const usTradesStatus = usReadLine('trades', us.current?.fills);
  const usUnlisted = us.current?.fills?.unlistedFormerMembers ?? 0;
  const usTradesLine = (trades.length > 0 || fillsLoading) && usTradesStatus !== null;
  // "Reading…" only beside US rows, which show while K/P fills are out; an org
  // without US keeps the last read's fills on a Refresh with no line, as before.
  const usTradesReading = fillsLoading && us.fills.rows.length > 0;
  // #1386: the trades panel names its own failed K/P read, as the orders panel does.
  const kTradesNote = fillsPanel.disclosure === 'retained'
    ? 'Kalshi and Polymarket trades did not refresh — this view uses their last confirmed list.'
    : fillsPanel.disclosure === 'unavailable' ? 'Kalshi and Polymarket trades did not load.' : null;
  const kTradesNoteInline = trades.length === 0 && usTradesStatus === null && fillsPanel.disclosure === 'unavailable';
  const kTradesNoteBelow = kTradesNote !== null && !kTradesNoteInline
    && !(fillsLoading && (trades.length === 0 || fillsPanel.disclosure === 'unavailable'));
  const tradeNotes = kTradesNoteBelow || usTradesLine || usTradesReading || trades.length > TRADES_SHOWN
    || us.current?.fills?.complete === false || us.fills.hidden + us.fills.pending > 0;

  // #1386: the orders panel names its own failed read, beside (never instead
  // of) what the US reads could not give; "No open orders" only after a valid read.
  // A panel heading counts the rows listed under it, and reads "—" only when its
  // read failed with nothing listed: a failed K/P read never blanks the count of
  // US rows that are shown. The summary tiles are the K/P-confirmed totals.
  const listedOrders = visibleOrders.length + institutionalRows.length;
  const ordersSpinner = listedOrders === 0 && ordersLoading;
  const kOrdersNote = ordersPanel.disclosure === 'retained'
    ? 'Kalshi open orders did not refresh — this view uses the last confirmed Kalshi list.'
    : ordersPanel.disclosure === 'unavailable' ? 'Kalshi open orders did not load.' : null;
  const ordersUsEmpty = !usCurrent ? 'Checking Polymarket US…'
    : usCurrent.accounts === 'failed' ? 'US account availability could not be checked'
    : institutionalDesk.failed ? 'US orders unavailable'
    : institutionalDesk.account && !institutionalDesk.data?.snapshot ? 'No saved US orders yet'
    : null;
  // With nothing listed and nothing from US to say, a failed first read IS the
  // empty line; a retained list filtered to nothing still reads "No open orders".
  const kOrdersNoteInline = listedOrders === 0 && ordersUsEmpty === null && ordersPanel.disclosure === 'unavailable';
  // Unlike positions and trades, orders has no "Reading…" line, so its failure
  // line stays through a re-read (behind the spinner when nothing is listed).
  const kOrdersNoteBelow = kOrdersNote !== null && !ordersSpinner && !kOrdersNoteInline;

  const masked = !showPnl;

  return (
    <div className="h-full overflow-auto p-6">
      <div className="flex gap-6">
        {/* Main column — full width */}
        <div className="min-w-0 flex-1 space-y-5">
          {/* Sticky toolbar — same flush-pin pattern as the Positions page. */}
          <div className="border-border/70 bg-background/95 supports-[backdrop-filter]:bg-background/85 sticky -top-6 z-30 -mx-6 -mt-6 border-b px-6 pb-3 pt-6 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded px-2 py-1 text-xs"
                >
                  <ArrowLeft size={14} /> Back
                </button>
              )}
              <Building2 size={20} className="text-muted-foreground" />
              <h1 className="text-xl font-semibold">{orgName ?? 'Organization'}</h1>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-muted-foreground flex items-center gap-1 text-[11px]">
                Owner
                <select
                  aria-label="Filter org money surfaces by owner"
                  value={ownerFilter}
                  onChange={(event) => setOwnerFilter(event.target.value as OwnerFilterKey)}
                  className="bg-card border-border text-foreground rounded-md border px-2 py-1 text-xs"
                >
                  {ownerOptions.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </select>
              </label>
              <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Filter by sport">
                <SportFilterChip
                  mode={sportFilter.size === 0 ? 'include' : 'off'}
                  label="All sports"
                  onClick={() => setSportFilter(new Map())}
                />
                {SPORT_TABS.map((s) => (
                  <SportFilterChip
                    key={s.id}
                    mode={sportFilter.get(s.id) ?? 'off'}
                    label={s.label}
                    onClick={() => setSportFilter((prev) => cycleSportFilter(prev, s.id))}
                  />
                ))}
              </div>
              <button
                onClick={refreshAll}
                aria-label="Refresh"
                // Re-arm only when every panel has landed — clicking mid-flight
                // would stack fresh fills-by-owner walks on a busy relay.
                disabled={loading || ordersLoading || fillsLoading}
                className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
              >
                <RefreshCw
                  size={14}
                  className={loading || ordersLoading || fillsLoading ? 'animate-spin' : ''}
                />
              </button>
            </div>
          </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
          )}

          {/* Cross-instance attribution sync health (master instances only —
              empty everywhere else). A stale sibling means its traders' new
              orders are temporarily showing as "unattributed", not missing. */}
          {syncSiblings.length > 0 && (
            <div className="text-muted-foreground flex flex-wrap items-center gap-2 px-1 text-[11px]">
              <span>Instance sync</span>
              {syncSiblings.map((s) => {
                const baseMs = syncFetchedAt ? new Date(syncFetchedAt).getTime() : Date.now();
                const ageS = s.lastOkAt
                  ? Math.max(0, Math.round((baseMs - new Date(s.lastOkAt).getTime()) / 1000))
                  : null;
                const stale = ageS == null || ageS > 120 || s.lastError != null;
                return (
                  <span
                    key={s.code}
                    className={`rounded border px-1.5 py-0.5 ${
                      stale
                        ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
                        : 'border-border'
                    }`}
                    title={
                      s.lastError ??
                      `${feedEndpointLabel(s.url)} — ${s.rowsMirrored} rows mirrored since relay start`
                    }
                  >
                    {s.code} ·{' '}
                    {ageS == null
                      ? 'never synced'
                      : ageS < 90
                        ? `${ageS}s ago`
                        : `${Math.round(ageS / 60)}m ago`}
                  </span>
                );
              })}
            </div>
          )}

          {/* Position economics come from the combined fills ledger. Balance
              and resting orders remain venue-native Kalshi reads. */}
          <SummaryStrip>
            <SummaryTile
              label="Org Balance (Kalshi · venue-reported)"
              value={balanceCents != null ? fmtUsd(balanceCents / 100) : '—'}
              title={kalshiReportedBalanceTitle('org')}
            />
            <SummaryTile
              label={`Open Positions (${usCounted ? 'K+P+US' : 'K+P'})`}
              value={accountComplete ? orgPanelTileCount(positionsPanel, shownPositionCount) : '—'}
              title={usCounted ? `Count includes Polymarket US holdings${
                us.current?.positions?.complete === false ? '; the Polymarket US read is incomplete, so there may be more' : ''}` : undefined}
            />
            <SummaryTile
              label={`Notional (${economicsVenues})${agg.notionalCents === null ? ' · incomplete' : ''}`}
              value={agg.notionalCents === null ? '—' : fmtUsd(agg.notionalCents / 100)}
              title="Recorded entry basis for the shown holdings; withheld if any contributing US ledger or basis is unavailable."
            />
            <SummaryTile label="Open Orders (Kalshi)" value={orgPanelTileCount(ordersPanel, visibleOrders.length)} />
            <SummaryTile
              label={`Trades (${usTradesCounted ? 'K+P+US' : 'K+P'})`}
              value={orgPanelTileCount(fillsPanel, trades.length)}
              title={usTradesCounted ? `Polymarket US: the org's latest 20 trades${
                us.current?.fills?.complete === false ? '; the Polymarket US read is incomplete' : ''}` : undefined}
            />
            <SummaryTile
              label={`Unrealized P&L (${economicsVenues})${!agg.kpKnown ? ' · incomplete'
                : agg.unrealizedComplete ? '' : ` · ${agg.pricedPositions}/${agg.totalPositions} priced`}`}
              value={masked ? '•••' : agg.unrealizedComplete && agg.unrealizedCents !== null ? fmtUsdSigned(agg.unrealizedCents / 100) : '—'}
              title="Shown holdings at current marks, less recorded opening fees and estimated closing fees. Incomplete US history or an expired exact-market quote withholds the total."
              tone={masked || !agg.unrealizedComplete || agg.unrealizedCents === null ? 'default' : agg.unrealizedCents >= 0 ? 'positive' : 'danger'}
            />
            {selectedMember && selectedMemberPnl && (
              <SummaryTile
                label={`Trader P&L (${economicsVenues}, net · all sports)${selectedMemberPnl.valueCents == null ? ' · fee/mark incomplete' : ''}`}
                value={masked ? '•••' : selectedMemberPnl.valueCents == null ? '—' : fmtUsdSigned(selectedMemberPnl.valueCents / 100)}
                tone={
                  masked || selectedMemberPnl.valueCents == null
                    ? 'default'
                    : selectedMemberPnl.valueCents >= 0
                      ? 'positive'
                      : 'danger'
                }
              />
            )}
            {selectedMember && selectedMemberPnl && (
              <SummaryTile
                label={`Realized fees (${economicsVenues} · all sports)${selectedMemberPnl.feesCents == null ? ' · incomplete' : ''}`}
                value={
                  masked
                    ? '•••'
                    : selectedMemberPnl.feesCents == null
                      ? '—'
                      : fmtUsd(selectedMemberPnl.feesCents / 100)
                }
              />
            )}
          </SummaryStrip>

          {/* Org positions — attributed. Same view-mode choices as /positions
              (shared positionViewMode); the table keeps the full attributed
              column layout, cards/compact carry the owner via `attribution`. */}
          <div className="space-y-2" data-testid="org-detail-open-positions">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-foreground text-sm font-medium">
                Open Positions <span className="text-muted-foreground">· {(positionsPanel.disclosure === 'unavailable' || kAccountNote !== null) && shownPositionCount === 0 ? '—' : shownPositionCount}</span>
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleShowPnl}
                  className="text-muted-foreground hover:text-foreground rounded p-1"
                  title={showPnl ? 'Hide P&L' : 'Show P&L'}
                >
                  {showPnl ? <Eye size={13} /> : <EyeOff size={13} />}
                </button>
                {shownPositionCount > 0 && (
                  <ViewModeToggle
                    value={positionViewMode}
                    modes={POSITION_VIEW_MODES}
                    onChange={setPositionViewMode}
                  />
                )}
              </div>
            </div>
            {positionsSpinner ? (
              <div className="bg-card border-border flex justify-center rounded-lg border py-6">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : shownPositionCount === 0 ? (
              <div className="bg-card border-border rounded-lg border">
                {/* "No open positions" only once US has answered too. */}
                <p className="text-muted-foreground px-4 py-6 text-center text-sm">
                  {usPositionsStatus ?? (kPositionsNoteInline ? kPositionsNote : kAccountNoteInline ? kAccountNote
                    : kAccountReadingInline ? kAccountReading : 'No open positions')}</p>
              </div>
            ) : positionViewMode === 'card' || positionViewMode === 'mini' ? (
              <CardGrid variant={positionViewMode === 'mini' ? 'mini' : 'default'}>
                {visiblePositions.map((p) => {
                  const vm = orgPositionVM(
                    p,
                    nowCentsFor(p.venue, p.ticker, p.side),
                    polyFeeSchedules.get(p.ticker),
                  );
                  return (
                    <PositionCard
                      key={orgRowKey(p)}
                      vm={vm}
                      hidePnl={!showPnl}
                      mini={positionViewMode === 'mini'}
                      onEdit={() => {}}
                      attribution={<OwnerTag owner={p.owner} />}
                    />
                  );
                })}
                {usPositions.map(({ p, vm }) => (
                  <PositionCard
                    key={vm.key}
                    vm={vm}
                    hidePnl={!showPnl}
                    mini={positionViewMode === 'mini'}
                    onEdit={() => {}}
                    attribution={<OwnerTag owner={p.owner} former={p.former} />}
                  />
                ))}
              </CardGrid>
            ) : positionViewMode === 'compact' ? (
              <CompactListGrid>
                {visiblePositions.map((p) => {
                  const vm = orgPositionVM(
                    p,
                    nowCentsFor(p.venue, p.ticker, p.side),
                    polyFeeSchedules.get(p.ticker),
                  );
                  return (
                    <CompactPositionItem
                      key={orgRowKey(p)}
                      vm={vm}
                      hidePnl={!showPnl}
                      onEdit={() => {}}
                      attribution={<OwnerTag owner={p.owner} />}
                    />
                  );
                })}
                {usPositions.map(({ p, vm }) => (
                  <CompactPositionItem
                    key={vm.key}
                    vm={vm}
                    hidePnl={!showPnl}
                    onEdit={() => {}}
                    attribution={<OwnerTag owner={p.owner} former={p.former} />}
                  />
                ))}
              </CompactListGrid>
            ) : (
              <div className="bg-card border-border rounded-lg border">
              <table className="w-full">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-left">Venue</th>
                    <th className="px-3 py-2 text-left">Owner</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Avg</th>
                    <th className="px-3 py-2 text-right">Last fill</th>
                    <th className="px-3 py-2 text-right">Now</th>
                    <th className="px-3 py-2 text-right">Value</th>
                    <th className="px-3 py-2 text-right">P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePositions.map((p) => {
                    const now = nowCentsFor(p.venue, p.ticker, p.side);
                    const vm = orgPositionVM(p, now, polyFeeSchedules.get(p.ticker));
                    const marketLabel = vm.title;
                    const betTeam = vm.team;
                    const pnlCents = vm.pnlDollars != null ? vm.pnlDollars * 100 : null;
                    return (
                      <tr key={orgRowKey(p)} className="border-border/40 border-b">
                        <td className="text-foreground px-4 py-2 text-xs">{marketLabel}</td>
                        <td className="px-3 py-2"><VenueTag venue={p.venue} /></td>
                        <td className="px-3 py-2"><OwnerTag owner={p.owner} /></td>
                        <td className="px-3 py-2 text-center"><SideBadge side={p.side} betTeam={betTeam} marketType={vm.marketType} /></td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {Number(p.qty.toFixed(2)).toLocaleString('en-US')}
                        </td>
                        <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">{p.avgCents}¢</td>
                        <td
                          className="text-muted-foreground px-3 py-2 text-right font-mono text-xs"
                          title={p.lastFillAt ?? undefined}
                        >
                          {fmtLastFillAt(p.lastFillAt)}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {now != null ? `${now}¢` : '—'}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {fmtUsd(p.costBasisCents / 100)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-mono text-xs ${
                            masked || pnlCents == null
                              ? 'text-muted-foreground'
                              : pnlCents >= 0
                                ? 'text-positive'
                                : 'text-negative'
                          }`}
                        >
                          {masked ? '•••' : pnlCents != null ? fmtUsdSigned(pnlCents / 100) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {usPositions.map(({ p, vm }) => (
                    <UsPositionTableRow key={vm.key} p={p} vm={vm} masked={masked} />
                  ))}
                </tbody>
              </table>
              </div>
            )}
            {usPositionNotes && (
              <div className="space-y-0.5 text-xs" data-testid="org-detail-positions-notes">
                {kPositionsNoteBelow && <p role="status" className="text-amber-500 light:text-amber-700 px-1">{kPositionsNote}</p>}
                {kAccountNoteBelow && <p role="status" className="text-amber-500 light:text-amber-700 px-1">{kAccountNote}</p>}
                {kAccountReadingBelow && <p className="text-muted-foreground px-1">{kAccountReading}</p>}
                {usPositionsReading && <p className="text-muted-foreground px-1">Reading Kalshi and Polymarket positions…</p>}
                {usPositionsLine && <p className="px-1">{usPositionsStatus}</p>}
                {us.current?.positions?.complete === false && (
                  <p className="text-muted-foreground px-1" data-testid="org-detail-us-positions-incomplete">
                    {usReadStatus('positions', 'incomplete').text} — some holdings may not be listed
                  </p>
                )}
                <FilterNote hidden={us.positions.hidden} pending={us.positions.pending} noun="Polymarket US position" what="sport"
                  testId="org-detail-us-positions-filter-note" />
              </div>
            )}
          </div>

          {/* Org orders — attributed */}
          <div className="bg-card border-border rounded-lg border" data-testid="org-detail-open-orders">
            <div className="border-border flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
              <h2 className="text-foreground text-sm font-medium">
                Open Orders <span className="text-muted-foreground">· {ordersPanel.disclosure === 'unavailable' && listedOrders === 0 ? '—' : listedOrders}</span>
              </h2>
              <InstitutionalDeskOrderControls desk={institutionalDesk} />
            </div>
            <InstitutionalOrderStatus status={institutionalOrderStatus} />
            <InstitutionalPrivateSnapshot account={institutionalDesk.account} orgKey={orgQueryParam} refreshKey={usRefresh} />
            {ordersSpinner ? (
              <div className="flex justify-center py-6">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : listedOrders === 0 ? (
              <p className="text-muted-foreground px-4 py-6 text-center text-sm">
                {ordersUsEmpty ?? (kOrdersNoteInline ? kOrdersNote : 'No open orders in this view')}</p>
            ) : (
              <table className={`w-full${institutionalDesk.account ? ' table-fixed' : ''}`}>
                {institutionalDesk.account && <colgroup><col style={{ width: '32%' }} /><col style={{ width: '15%' }} /><col style={{ width: '20%' }} /><col style={{ width: '20%' }} /><col style={{ width: '13%' }} /></colgroup>}
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-left">Owner</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((o, i) => {
                    const { marketLabel, betTeam: fullBetTeam, badgeTeam, marketType } = describeMarket(o.ticker, o.side);
                    const betTeam = badgeTeam ?? fullBetTeam;
                    return (
                      <tr key={o.orderId || `${o.ticker}-${o.side}-${i}`} className="border-border/40 border-b">
                        <td className="text-foreground px-4 py-2 text-xs">{marketLabel}</td>
                        <td className="px-3 py-2"><OwnerTag owner={o.owner} /></td>
                        <td className="px-3 py-2 text-center"><SideBadge side={o.side} betTeam={betTeam} marketType={marketType} /></td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {Number(o.qty.toFixed(2)).toLocaleString('en-US')}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {o.priceCents != null ? `${o.priceCents}¢` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {institutionalRows.map(order => <InstitutionalDeskOrderRow key={`institutional:${institutionalDesk.account!.id}:${order.orderId}`}
                    order={order} accountId={institutionalDesk.account!.id}
                    checkStatus={() => { void institutionalOrderStatus.check(order); }} canCheckStatus={institutionalOrderStatus.canCheck} />)}
                </tbody>
              </table>
            )}
            {kOrdersNoteBelow && <p role="status" className="text-amber-500 light:text-amber-700 px-4 py-2 text-xs">{kOrdersNote}</p>}
            {usCurrent?.accounts === 'failed' && listedOrders > 0 && <p role="status" className="text-amber-500 light:text-amber-700 px-4 py-2 text-xs">
              US account availability could not be checked; this list may be incomplete.
            </p>}
            {institutionalRows.length > 0 && sportFilter.size > 0 && <p className="text-muted-foreground px-4 py-2 text-xs">
              US symbols without a confirmed sport remain visible.
            </p>}
          </div>

          {/* Org trades/fills — owner-tagged, including unattributed rows; #1227:
              the org's Polymarket US trades too, newest first with the rest. */}
          <div className="bg-card border-border rounded-lg border" data-testid="org-detail-trades">
            <div className="border-border flex items-center justify-between gap-2 border-b px-4 py-2.5">
              <h2 className="text-foreground text-sm font-medium">
                Trades <span className="text-muted-foreground">· {fillsPanel.disclosure === 'unavailable' && trades.length === 0 ? '—' : trades.length}</span>
              </h2>
              {usTradesCounted && (
                <span className="text-muted-foreground text-[11px]" data-testid="org-detail-us-trades-cap">
                  Polymarket US: the org&apos;s latest 20
                </span>
              )}
            </div>
            {trades.length === 0 && fillsLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : trades.length === 0 ? (
              // "No trades" only once US has answered too.
              <p className="text-muted-foreground px-4 py-6 text-center text-sm">
                {usTradesStatus ?? (kTradesNoteInline ? kTradesNote : 'No trades')}</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-left">Venue</th>
                    <th className="px-3 py-2 text-left">Owner</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-center">Action</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">Fee</th>
                    <th className="px-4 py-2 text-right">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.slice(0, TRADES_SHOWN).map((row, i) => {
                    if (row.kind === 'us') return <UsTradeTableRow key={`us|${row.f.accountId}|${row.f.executionId}`} f={row.f} />;
                    const f = row.f;
                    const polyMarket = f.venue === 'polymarket'
                      ? describePolyMarketCached(f.ticker)
                      : null;
                    const kalshiMarket = f.venue === 'kalshi'
                      ? describeMarket(f.ticker, f.side)
                      : null;
                    const marketLabel = polyMarket?.matchup ?? kalshiMarket?.marketLabel ?? f.ticker;
                    const betTeam = kalshiMarket?.badgeTeam ?? kalshiMarket?.betTeam ?? null;
                    const when = f.executedAt ? new Date(f.executedAt) : null;
                    const timeLabel = when && !Number.isNaN(when.getTime()) ? when.toLocaleString() : '—';
                    return (
                      <tr key={f.fillId || `${f.ticker}-${f.side}-${i}`} className="border-border/40 border-b">
                        {/* #1227: a long unbroken label (a raw condition id) wraps here
                            rather than pushing a US row's exact size out of the card. */}
                        <td className="text-foreground px-4 py-2 text-xs [overflow-wrap:anywhere]">{marketLabel}</td>
                        <td className="px-3 py-2"><VenueTag venue={f.venue} /></td>
                        <td className="px-3 py-2"><OwnerTag owner={f.owner} /></td>
                        <td className="px-3 py-2 text-center"><SideBadge side={f.side} betTeam={betTeam} marketType={kalshiMarket?.marketType} /></td>
                        <td className="px-3 py-2 text-center">
                          <span className={f.action === 'buy' ? 'text-emerald-400' : 'text-amber-400 light:text-amber-700'}>
                            {f.action.toUpperCase()}
                          </span>
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {Number(f.qty.toFixed(2)).toLocaleString('en-US')}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {f.priceCents != null ? `${f.priceCents}¢` : '—'}
                        </td>
                        <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                          {f.feeCents != null ? fmtUsd(f.feeCents / 100) : '—'}
                        </td>
                        <td className="text-muted-foreground px-4 py-2 text-right text-[11px]">{timeLabel}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {tradeNotes && (
              <div className="space-y-0.5 px-4 pb-2 pt-1 text-xs" data-testid="org-detail-trades-notes">
                {kTradesNoteBelow && <p role="status" className="text-amber-500 light:text-amber-700">{kTradesNote}</p>}
                {trades.length > TRADES_SHOWN && (
                  <p className="text-muted-foreground" data-testid="org-detail-trades-cut">
                    Showing the newest {TRADES_SHOWN} of {trades.length}
                  </p>
                )}
                {usTradesReading && <p className="text-muted-foreground">Reading Kalshi and Polymarket trades…</p>}
                {usTradesLine && <p>{usTradesStatus}</p>}
                {us.current?.fills?.complete === false && (
                  <p className="text-muted-foreground" data-testid="org-detail-us-trades-incomplete">
                    {usReadStatus('trades', 'incomplete').text} — some trades may not be listed
                    {usUnlisted > 0 ? ` · ${usUnlisted} former member${usUnlisted === 1 ? "'s" : "s'"} trades not listed` : ''}
                  </p>
                )}
                <div className="-mx-4">
                  <FilterNote hidden={us.fills.hidden} pending={us.fills.pending} noun="Polymarket US trade" what="sport"
                    testId="org-detail-us-trades-filter-note" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar — compact per-trader rollup (the "who"), built to fit
            the narrow rail (the full OrgTradersTable's 6 columns overflowed it). */}
        <aside className="sticky top-6 hidden w-72 shrink-0 self-start lg:block">
          <div className="bg-card border-border rounded-lg border">
            <div className="border-border border-b px-3 py-2.5 text-sm font-medium">
              Members <span className="text-muted-foreground">· {positionsPanel.disclosure === 'unavailable' && members.length === 0 ? '—' : members.length}</span>
            </div>
            <div className="max-h-[calc(100vh-9rem)] overflow-y-auto">
              {members.length === 0 && loading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                </div>
              ) : members.length === 0 ? (
                <p className="text-muted-foreground px-3 py-6 text-center text-sm">
                  {positionsPanel.disclosure === 'unavailable' ? 'Members did not load' : 'No members'}</p>
              ) : (
                members.map((m) => {
                  const unrealized = computeOrgTraderUnrealized(
                    m,
                    getMarketPrice,
                    undefined,
                    (ticker) => polyFeeSchedules.get(ticker),
                  );
                  const memberPnl =
                    addKnown(addKnown(m.realizedPnlNetCents, usMemberEconomics.get(m.userId)!.realizedNetCents),
                      addKnown(unrealized.valueCents, usMemberEconomics.get(m.userId)!.unrealizedCents));
                  const usSummary = usMemberEconomics.get(m.userId)!;
                  const memberCount = addKnown(m.openCount, usSummary.openCount);
                  const memberNotional = addKnown(m.openNotionalCents, usSummary.notionalCents);
                  return (
                    <button
                      key={m.userId}
                      onClick={() => onTraderClick(m)}
                      className="border-border/40 hover:bg-muted/40 w-full border-b px-3 py-2 text-left transition-colors last:border-0"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-foreground truncate text-xs font-medium">{m.username}</span>
                        <span
                          className={`shrink-0 font-mono text-xs ${
                            masked || memberPnl == null
                              ? 'text-muted-foreground'
                              : memberPnl >= 0
                                ? 'text-positive'
                                : 'text-negative'
                          }`}
                          title={
                            memberPnl !== null
                              ? 'Net realized after recorded fees, plus marked unrealized P&L'
                              : 'Combined P&L withheld: a contributing ledger, fee, or current market quote is incomplete.'
                          }
                        >
                          {masked ? '•••' : memberPnl == null ? '—' : fmtUsdSigned(memberPnl / 100)}
                        </span>
                      </div>
                      <div className="text-muted-foreground mt-0.5 text-[11px]">
                        {economicsVenues}: {memberCount ?? '—'} ctr · {memberNotional === null ? '—' : fmtUsd(memberNotional / 100)} · K: {m.openOrderCount ?? '\u2014'} ord
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
